import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react'

import { env } from '@/config/env'
import { setUnauthorizedHandler } from '@/services/api/client'
import { authApi } from '@/services/api/endpoints/authApi'
import type { LoginRequest, User } from '@/services/api/schemas/auth'
import { tokenStorage } from '@/services/api/tokenStorage'

import { AuthContext, type AuthContextValue, type AuthStatus } from './AuthContext'

interface AuthState {
  status: AuthStatus
  user: User | null
}

function initialState(): AuthState {
  if (!env.authEnabled) return { status: 'authenticated', user: null }
  return tokenStorage.getAccess()
    ? { status: 'loading', user: null }
    : { status: 'anonymous', user: null }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<AuthState>(initialState)

  // Потеря сессии (refresh не удался) приходит из axios-интерсептора.
  useEffect(() => {
    if (!env.authEnabled) return
    setUnauthorizedHandler(() => {
      queryClient.clear()
      setState({ status: 'anonymous', user: null })
    })
    return () => setUnauthorizedHandler(null)
  }, [queryClient])

  // При старте с сохранённым токеном проверяем его запросом профиля.
  useEffect(() => {
    if (state.status !== 'loading') return
    let cancelled = false
    authApi
      .me()
      .then((user) => {
        if (!cancelled) setState({ status: 'authenticated', user })
      })
      .catch(() => {
        tokenStorage.clear()
        if (!cancelled) setState({ status: 'anonymous', user: null })
      })
    return () => {
      cancelled = true
    }
  }, [state.status])

  const login = useCallback(async (credentials: LoginRequest) => {
    const response = await authApi.login(credentials)
    tokenStorage.set(response)
    setState({ status: 'authenticated', user: response.user })
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout(tokenStorage.getRefresh())
    } catch {
      /* сессию сбрасываем локально в любом случае */
    }
    tokenStorage.clear()
    queryClient.clear()
    setState({ status: 'anonymous', user: null })
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({ status: state.status, user: state.user, login, logout }),
    [state, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
